/**
 * Helper functions for creating field mappings in table data hooks
 */

/**
 * Creates field mappings for status fields that are inferred from deletedAt
 *
 * @param statusField - The frontend field name (e.g., 'status')
 * @param deletedAtField - The backend field name (e.g., 'deletedAt')
 * @param activeValue - The value that represents active status (e.g., '1')
 * @returns Field mappings object for useTableData
 */
export function createStatusFieldMappings(
  statusField: string = 'status',
  deletedAtField: string = 'deletedAt',
  activeValue: string = '1',
) {
  return {
    // Map status field to deletedAt for sorting
    sorting: (field: string) => (field === statusField ? deletedAtField : field),
    // Map status field to deletedAt for filtering
    filters: (field: string, value: unknown) => {
      if (field === statusField) {
        // Map status values to deletedAt values
        // activeValue for active (deletedAt is null), anything else for inactive (deletedAt is not null)
        return {
          field: deletedAtField,
          value: value === activeValue ? null : 'not_null',
        }
      }
      return { field, value }
    },
  }
}

/**
 * Creates field mappings for custom field transformations
 *
 * @param mappings - Object with field mappings
 * @returns Field mappings object for useTableData
 */
export function createCustomFieldMappings(mappings: {
  sorting?: Record<string, string>
  filters?: Record<string, (value: unknown) => { field: string; value: unknown } | null>
}) {
  return {
    sorting: (field: string) => mappings.sorting?.[field] ?? field,
    filters: (field: string, value: unknown) => {
      const filterMapping = mappings.filters?.[field]
      if (filterMapping) {
        return filterMapping(value)
      }
      return { field, value }
    },
  }
}

/**
 * Combines multiple field mappings into one
 *
 * @param mappings - Array of field mappings
 * @returns Combined field mappings object for useTableData
 */
export function combineFieldMappings(
  ...mappings: Array<{
    sorting?: (field: string) => string
    filters?: (field: string, value: unknown) => { field: string; value: unknown } | null
  }>
) {
  return {
    sorting: (field: string) => {
      let result = field
      for (const mapping of mappings) {
        if (mapping.sorting) {
          result = mapping.sorting(result)
        }
      }
      return result
    },
    filters: (field: string, value: unknown) => {
      let result = { field, value }
      for (const mapping of mappings) {
        if (mapping.filters) {
          const mapped = mapping.filters(result.field, result.value)
          if (mapped) {
            result = mapped
          }
        }
      }
      return result
    },
  }
}
